import { updateValuesWrapper } from "@repo/dynamicEditor/src/helpers/updatePropertiesHooks";
import { PushProcessingNotification } from "@repo/helpers";
import { ComplaintCustomerAction, keyPair } from "@repo/models";
import { useCallback, useMemo } from "react";

import { useComplaintApi, useComplaintQueries } from "../../../../api/complaintApi";
import { useComplaintContext, useComplaintCustomerActionContext } from "../../../../contexts/editableItemContext";
import { modals } from "../../../../string";
import { useIncidentLists } from "../../../../tabs/repIncident/incidentLists";
import { EditableItemDynamicTab } from "../../dynamicTab/editableItem";
import { ITemplateData } from "../../templateDataHook";

interface IState extends ITemplateData {
	customerAction: ComplaintCustomerAction;
}

export default function CustomerActionEditor() {
	const { item: complaint } = useComplaintContext();
	const context = useComplaintCustomerActionContext();
	const { item: customerAction, clearItem } = context;
	const { saveCustomerActionTaken } = useComplaintApi();
	const { useCustomerActionQuery } = useComplaintQueries();
	const { clearQuery } = useCustomerActionQuery(complaint.id);
	const [customerActionLists] = useCustomerActionLists();
	const templateData = useMemo(
		() => ({ customerAction, customerActionLists }),
		[customerAction, customerActionLists],
	);

	const save = useCallback(async () => {
		const notify = PushProcessingNotification("Saving Customer Action");
		try {
			await saveCustomerActionTaken(customerAction);
			notify.success("Saved!");
			return undefined;
		} catch (ex) {
			notify.error("Failed to save");
			throw ex;
		}
	}, [customerAction]);

	const postSave = useCallback(async () => {
		clearItem();
		await clearQuery();
	}, [clearItem, clearQuery]);

	const updateValues = useMemo(() => updateValuesWrapper(applyBusinessLogic), [applyBusinessLogic]);

	async function updateValueInternal(id: string, value: any, currentState: IState, parentComponent?: string) {
		const { newState } = await updateValues(id, value, currentState, parentComponent);
		return newState ?? currentState;
	}

	async function applyBusinessLogic(id: string, value: any, newState: IState) {
		async function update(name: string, value: any) {
			newState = await updateValueInternal(name, value, newState);
		}
		//Custom logic
		switch (id) {
			case "customerAction.actionTakenCategoryOther":
				//if (value === "")
				//    update(id, null);
				break;
			default:
				break;
		}

		return newState as IState;
	}

	if (!customerAction) return null;

	return (
		<EditableItemDynamicTab
			itemContext={context}
			pageName={modals.customerActionForm}
			otherTemplateData={templateData}
			itemProp="customerAction"
			updateValues={updateValues}
			save={save}
			postSave={postSave}
			submitButtonText="Save Customer Action"
			loaded
		/>
	);
}

export function useCustomerActionLists() {
	const [incidentLists, incidentListsLoaded] = useIncidentLists();
	const { useCustomerActionCategoriesQuery } = useComplaintQueries();

	const { item: customerAction } = useComplaintCustomerActionContext();
	const { data: categoriesQuery, loaded: actionTakenCategoriesLoaded } = useCustomerActionCategoriesQuery();

	const actionTakenCategories = categoriesQuery.map((c) => {
		return { id: c.Id, value: c.Name } as keyPair;
	});
	const actionTakenSubCategories = categoriesQuery.get(customerAction?.actionTakenCategoryId)?.SubCategories ?? [];

	const activeLists = useMemo(() => {
		return {
			actionTakenCategories,
			actionTakenSubCategories,
			statuses: incidentLists.statuses,
		};
	}, [actionTakenCategoriesLoaded, customerAction, incidentListsLoaded]);

	return [activeLists];
}
